import { BrandLogo } from '@funkihoo/ui-web';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  NavLink,
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from 'react-router-dom';
import { clearBearer, getStoredBearer, useSignOutMutation } from './api/auth.js';
import { SentryErrorBoundary } from './lib/sentry.js';
import { AccountMergeRoute } from './routes/account-merge.js';
import { AccountTotpRoute } from './routes/account-totp.js';
import { BanAppealsRoute } from './routes/ban-appeals.js';
import { ModerationQueueRoute } from './routes/moderation-queue.js';
import { PricingEditorRoute } from './routes/pricing-editor.js';
import { PromoRoute } from './routes/promo.js';
import { PromotionalEventsRoute } from './routes/promotional-events.js';
import { ReportsRoute } from './routes/reports.js';
import { SignInRoute } from './routes/sign-in.js';
import { SupportTicketsRoute } from './routes/support-tickets.js';
import { UserDetailRoute } from './routes/user-detail.js';
import { WaitlistRoute } from './routes/waitlist.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

const navItems = [
  { to: 'moderation', label: 'Moderation queue' },
  { to: 'support-tickets', label: 'Support tickets' },
  { to: 'waitlist', label: 'Waitlist' },
  { to: 'promotional-events', label: 'Promo events' },
  { to: 'promo', label: 'Promo codes' },
  { to: 'reports', label: 'Reports' },
  { to: 'users', label: 'Users' },
  { to: 'pricing', label: 'Pricing' },
  { to: 'appeals', label: 'Ban appeals' },
  { to: 'merge', label: 'Merge accounts' },
  { to: 'account/totp', label: 'My TOTP' },
] as const;

/**
 * Renders the shell only when a bearer token is present. Without one,
 * redirects to /sign-in. Dev-mode `X-Test-Admin-Id` headers still ride
 * along (see [`apps/admin/src/api/client.ts`](apps/admin/src/api/client.ts)),
 * so backend integration tests + local dev keep working — but the UI
 * gate is enforced for any deployed environment.
 */
function AuthGate({ children }: { children: ReactNode }) {
  if (!getStoredBearer()) {
    return <Navigate to="/sign-in" replace />;
  }
  return <>{children}</>;
}

function SignOutButton() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signOut = useSignOutMutation();
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await signOut.mutateAsync();
        } catch {
          // Even if the server call fails, clear the local bearer.
        }
        clearBearer();
        qc.clear();
        navigate('/sign-in', { replace: true });
      }}
      className="mt-2 block w-full rounded-md border border-neutral-200 px-3 py-2 text-left text-neutral-700 text-sm hover:bg-neutral-100"
    >
      {signOut.isPending ? 'Signing out…' : 'Sign out'}
    </button>
  );
}

function Shell() {
  return (
    <AuthGate>
      <div className="flex min-h-screen">
        <aside className="flex w-60 flex-col border-r border-neutral-200 bg-white p-4">
          <div className="mb-6 inline-flex items-baseline gap-1.5">
            <BrandLogo size="sm" />
            <span className="font-display font-medium text-neutral-500 text-sm">· admin</span>
          </div>
          <nav className="flex-1">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-700 hover:bg-neutral-100',
                      ].join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <SignOutButton />
        </aside>
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </AuthGate>
  );
}

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInRoute /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Navigate to="moderation" replace /> },
      { path: 'moderation', element: <ModerationQueueRoute /> },
      { path: 'support-tickets', element: <SupportTicketsRoute /> },
      { path: 'waitlist', element: <WaitlistRoute /> },
      { path: 'promotional-events', element: <PromotionalEventsRoute /> },
      { path: 'promo', element: <PromoRoute /> },
      { path: 'reports', element: <ReportsRoute /> },
      { path: 'users', element: <UserDetailRoute /> },
      { path: 'pricing', element: <PricingEditorRoute /> },
      { path: 'appeals', element: <BanAppealsRoute /> },
      { path: 'merge', element: <AccountMergeRoute /> },
      { path: 'account/totp', element: <AccountTotpRoute /> },
    ],
  },
]);

/**
 * Top-level error boundary. Captures any unhandled React render error
 * to Sentry (when DSN is configured) and shows a minimal recovery
 * surface so the operator isn't staring at a blank page. The fallback
 * is intentionally austere — admin users are technical enough to copy
 * the error message and refresh; we don't try to auto-retry routes.
 */
function CrashFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6">
      <div className="max-w-md rounded-md border border-danger-200 bg-white p-6 shadow-tile">
        <h1 className="font-display font-semibold text-danger-700 text-lg">
          The admin app crashed.
        </h1>
        <p className="mt-2 text-neutral-700 text-sm">
          The error has been reported to Sentry. Reload the page to recover; if it persists, check
          the latest deploy on /admin in Sentry for context.
        </p>
        <pre className="mt-3 overflow-x-auto rounded bg-neutral-100 p-3 text-xs text-neutral-700">
          {message}
        </pre>
        <button
          type="button"
          onClick={resetError}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 font-medium text-sm text-white hover:bg-primary-700"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export function App() {
  return (
    <SentryErrorBoundary fallback={CrashFallback}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}
